// src/components/RecipeDetail/RecipeDetail.tsx
import React, { useMemo, useState } from 'react'
import styles from './RecipeDetail.module.css'
import ConfirmationDialog from '../../components/ConfirmationDialog'

interface Ingredient {
  quantity?: string | null
  unit?: string | null
  name: string
}

interface Instruction {
  order: number
  text: string
}

interface Recipe {
  id: string
  title: string
  ingredients: Ingredient[]
  instructions: Instruction[]
  type?: 'saved' | 'created' | 'variant'
  source_url?: string
}

interface RecipeDetailProps {
  recipe: Recipe
  isSaved: boolean
  onToggleSave: () => void
  onCustomize?: () => void
  onViewFullPage?: () => void
}

const RecipeDetail: React.FC<RecipeDetailProps> = ({
  recipe,
  isSaved,
  onToggleSave,
  onCustomize,
  onViewFullPage,
}) => {
  const [scale, setScale] = useState<number>(1)
  const [showLeaveSiteDialog, setShowLeaveSiteDialog] = useState(false)

  console.log("recipe", recipe)

  const hostname = useMemo(() => {
    if (!recipe.source_url) return null
    try {
      return new URL(recipe.source_url).hostname
    } catch {
      return recipe.source_url
    }
  }, [recipe.source_url])

  const handleSourceClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (recipe.source_url) {
      setShowLeaveSiteDialog(true)
    }
  }

  const handleLeaveConfirm = () => {
    setShowLeaveSiteDialog(false)
    if (recipe.source_url) {
      window.open(recipe.source_url, '_blank', 'noopener,noreferrer')
    }
  }

  const handleLeaveCancel = () => setShowLeaveSiteDialog(false)

  // Quantity parsing and formatting helpers
  const parseQuantity = (q: string | null | undefined): number | null => {
    if (!q) return null
    const s = String(q).trim()
    // mixed number e.g., 1 1/2
    let m = s.match(/^(\d+)\s+(\d+)[\/](\d+)/)
    if (m) {
      const whole = parseInt(m[1], 10)
      const num = parseInt(m[2], 10)
      const den = parseInt(m[3], 10) || 1
      return whole + num / den
    }
    // fraction e.g., 3/4
    m = s.match(/^(\d+)[\/](\d+)/)
    if (m) {
      const num = parseInt(m[1], 10)
      const den = parseInt(m[2], 10) || 1
      return num / den
    }
    // range e.g., 2-3 or 2 – 3: take first
    m = s.match(/^(\d+(?:\.\d+)?)/)
    if (m) return parseFloat(m[1])
    return null
  }

  const formatQuantity = (value: number): string => {
    // Snap to nearest 1/8 to keep nice fractions
    const eighths = Math.round(value * 8)
    const snapped = eighths / 8
    const whole = Math.floor(snapped)
    const frac = snapped - whole
    const denOptions = [2, 3, 4, 8]
    let bestNum = 0
    let bestDen = 1
    let bestErr = Infinity
    for (const den of denOptions) {
      const num = Math.round(frac * den)
      const err = Math.abs(frac - num / den)
      if (err < bestErr) { bestErr = err; bestNum = num; bestDen = den }
    }
    if (whole === 0 && bestNum === 0) return '0'
    if (bestNum === 0) return String(whole)
    if (whole === 0) return `${bestNum}/${bestDen}`
    return `${whole} ${bestNum}/${bestDen}`
  }

  const scaledIngredients = useMemo(() => {
    if (scale === 1) return recipe.ingredients
    return recipe.ingredients.map(ing => {
      const base = parseQuantity(ing.quantity)
      const nextQty = base != null ? formatQuantity(base * scale) : ing.quantity
      return { ...ing, quantity: nextQty }
    })
  }, [recipe.ingredients, scale])

  return (
    <div className={styles.recipeSection}>
      <h1 className={styles.title}>{recipe.title || 'Recipe'}</h1>
      {recipe.source_url && (
        <div className={styles.sourceRow}>
          <span className={styles.sourceLabel}>Source:</span>{' '}
          <a
            href={recipe.source_url}
            className={styles.sourceLink}
            onClick={handleSourceClick}
            rel="noopener noreferrer"
          >
            {hostname}
          </a>
        </div>
      )}
      <div className={styles.scaleRow}>
        <label htmlFor="scaleSelect" className={styles.scaleLabel}>Scale:</label>
        <select id="scaleSelect" className={styles.scaleSelect} value={scale} onChange={(e)=> setScale(parseFloat(e.target.value))}>
          <option value={0.5}>1/2x</option>
          <option value={1}>1x</option>
          <option value={2}>2x</option>
        </select>
      </div>
      <div className={styles.actionButtons}>
        <button className={styles.saveButton} onClick={onToggleSave}>
          {isSaved ? 'Unsave' : 'Save'}
        </button>
        {onCustomize && (
          <button className={styles.customizeButton} onClick={onCustomize}>
            {recipe.type === 'variant' ? 'Edit' : 'Customize'}
          </button>
        )}
        {onViewFullPage && (
          <button className={styles.viewFullButton} onClick={onViewFullPage}>
            View full page
          </button>
        )}
      </div>

      <section>
        <h2 className={styles.subheading}>Ingredients</h2>
        <ul className={styles.list}>
          {scaledIngredients.map((item, idx) => (
            <li key={idx} className={styles.lineItem}>
              {item.quantity ?? ''} {item.unit ?? ''}{' '}
              {item.name}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className={styles.subheading}>Instructions</h2>
        <ol className={styles.list}>
          {recipe.instructions
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((step) => (
              <li key={step.order} className={styles.lineItem}>
                {step.text}
              </li>
            ))}
        </ol>
      </section>
      <ConfirmationDialog
        isOpen={showLeaveSiteDialog}
        title="You are leaving PlatefullAI"
        message="You are about to open the original recipe source in a new tab. Continue?"
        confirmText="Open Source"
        cancelText="Stay"
        onConfirm={handleLeaveConfirm}
        onCancel={handleLeaveCancel}
        type="danger"
      />
    </div>
  )
}

export default RecipeDetail